import React, { useState, useContext, useEffect } from 'react';
import { 
  ShoppingBag, 
  ChevronRight, 
  Clock, 
  Tag, 
  Search, 
  Plus, 
  Filter, 
  ChevronLeft, 
  Grid, 
  List, 
  Edit, 
  Trash2, 
  Eye,
  ArrowUpDown,
  CheckCircle,
  XCircle,
  MessageSquare,
  MoreVertical,
  Menu
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AppContext from '../../context/AppContext';

// Badge pour le statut du produit
const StatusBadge = ({ status }) => {
  if (status === 'active') {
    return (
      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center">
        <CheckCircle className="w-3 h-3 mr-1" />
        Actif
      </span>
    );
  }
  
  return (
    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium flex items-center">
      <XCircle className="w-3 h-3 mr-1" />
      Inactif
    </span>
  );
};

// Filtre des catégories
const CategoryFilter = ({ categories, selected, onChange }) => {
  return (
    <div className="flex gap-2 pb-2 overflow-x-auto hide-scrollbar">
      <button
        onClick={() => onChange('all')}
        className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
          selected === 'all' 
            ? 'bg-blue-600 text-white' 
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        Tous
      </button>
      
      {categories.map((category) => (
        <button
          key={category.id}
          onClick={() => onChange(category.id)}
          className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
            selected === category.id 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {category.name}
        </button>
      ))}
    </div>
  );
};

// Mode grille
const ProductGrid = ({ products, onProductClick, onActionClick }) => {
  return (
    <div className="grid grid-cols-2 gap-4">
      {products.map((product) => (
        <div 
          key={product.id}
          className="bg-white rounded-2xl overflow-hidden shadow-sm group hover:shadow-md transition-all duration-300"
        >
          <div 
            onClick={() => onProductClick(product)}
            className="cursor-pointer"
          >
            <div className="relative aspect-square">
              {product?.imageUrl ? (
                <img 
                  src={product.imageUrl} 
                  alt={product.title}
                  className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.parentNode.innerHTML = `<div class="w-full h-full bg-gray-100 flex items-center justify-center"><svg class="w-10 h-10 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 6 22M18 2 18 22M6 12a5 5 0 0 1 12 0Z"/></svg></div>`;
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <ShoppingBag className="w-10 h-10 text-gray-300" />
                </div>
              )}
              
              <div className="absolute top-2 right-2">
                <StatusBadge status={product.status || 'inactive'} />
              </div>
              
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onActionClick(product);
                }}
                className="absolute bottom-2 right-2 p-2 bg-black bg-opacity-30 backdrop-blur-sm rounded-full text-white hover:bg-opacity-50 transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            </div>
            
            <div className="p-3">
              <h3 className="font-medium text-gray-900 line-clamp-1 mb-1">{product.title}</h3>
              <div className="flex items-center justify-between">
                <span className="text-blue-600 font-bold">{product.price} FCFA</span>
                <div className="flex items-center text-xs text-gray-500">
                  <Eye className="w-3 h-3 mr-1" />
                  {product.views || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Mode liste
const ProductList = ({ products, onProductClick, onActionClick }) => {
  return (
    <div className="space-y-3">
      {products.map((product) => (
        <div 
          key={product.id}
          onClick={() => onProductClick(product)}
          className="bg-white rounded-xl overflow-hidden shadow-sm group hover:shadow-md transition-all duration-300 cursor-pointer"
        >
          <div className="flex">
            {/* Image container */}
            <div className="relative w-24 h-24 sm:w-28 sm:h-28">
              {product?.imageUrl ? (
                <img 
                  src={product.imageUrl} 
                  alt={product.title}
                  className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.parentNode.innerHTML = `<div class="w-full h-full bg-gray-100 flex items-center justify-center"><svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 6 22M18 2 18 22M6 12a5 5 0 0 1 12 0Z"/></svg></div>`;
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-gray-400" />
                </div>
              )}
              
              <div className="absolute bottom-1 left-1">
                <StatusBadge status={product.status || 'inactive'} />
              </div>
            </div>

            {/* Infos produit */}
            <div className="flex-1 p-3 flex flex-col justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900 line-clamp-1 mb-1">
                  {product.title}
                </h3>
                {product.description && (
                  <p className="text-xs text-gray-500 line-clamp-1">
                    {product.description}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between mt-1">
                <span className="text-sm font-bold text-blue-600">
                  {product.price} FCFA
                </span>
                {product.duration && (
                  <span className="text-xs text-gray-500 flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    {product.duration} min
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center pr-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onActionClick(product);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Options des produits (popup avec actions pour un produit)
const ProductOptions = ({ product, onClose, onEdit, onDelete, onView }) => {
  // Vérifier si l'application s'exécute dans le WebApp Telegram
  const isTelegramWebApp = typeof window !== 'undefined' && window.Telegram && window.Telegram.WebApp;
  
  // Optimisations pour Telegram WebApp
  useEffect(() => {
    if (isTelegramWebApp) {
      // Désactiver temporairement le swipe-to-back de Telegram
      window.Telegram.WebApp.disableClosingConfirmation();
      
      // Empêcher le défilement sur la page principale
      document.body.style.overflow = 'hidden';
      
      return () => {
        window.Telegram.WebApp.enableClosingConfirmation();
        document.body.style.overflow = '';
      };
    }
  }, [isTelegramWebApp]);
  
  // Titre sécurisé en cas de null
  const safeTitle = product?.title || 'Produit';
  const safeId = product?.id || '';
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-end justify-center animate-fadeIn backdrop-blur-sm safe-area-bottom" 
      onClick={onClose}
      style={{ 
        paddingBottom: isTelegramWebApp ? 'env(safe-area-inset-bottom, 0px)' : '0' 
      }}
    >
      <div 
        className="bg-white rounded-t-2xl p-4 w-full max-w-lg animate-slideUp"
        style={{ 
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto w-12 h-1 bg-gray-300 rounded-full mb-4" />
        
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{safeTitle}</h3>
        <p className="text-sm text-gray-500 mb-6">ID: {safeId}</p>
        
        <div className="space-y-2">
          <button 
            onClick={() => { onView(product); onClose(); }}
            className="flex items-center gap-3 w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 rounded-xl transition-colors"
          >
            <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
              <Eye className="w-5 h-5" />
            </div>
            <span className="font-medium">Voir le produit</span>
          </button>
          
          <button 
            onClick={() => { onEdit(product); onClose(); }}
            className="flex items-center gap-3 w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 rounded-xl transition-colors"
          >
            <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
              <Edit className="w-5 h-5" />
            </div>
            <span className="font-medium">Modifier</span>
          </button>
          
          <button 
            onClick={() => { onDelete(product); onClose(); }}
            className="flex items-center gap-3 w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 rounded-xl transition-colors text-red-600"
          >
            <div className="p-2 rounded-lg bg-red-100 text-red-600">
              <Trash2 className="w-5 h-5" />
            </div>
            <span className="font-medium">Supprimer</span>
          </button>
        </div>
        
        <button 
          onClick={onClose}
          className="w-full text-gray-700 font-medium p-4 mt-4 border-t border-gray-100 active:bg-gray-50"
        >
          Annuler
        </button>
      </div>
    </div>
  );
};

// Composant principal
const ProductsList = () => {
  const navigate = useNavigate();
  const { userProducts, id } = useContext(AppContext); // Récupérer aussi l'ID de l'utilisateur connecté
  console.log("DEBUG - userProducts:", userProducts); // Log des données brutes
  console.log("DEBUG - ID utilisateur connecté:", id); // Log de l'ID utilisateur
  const [searchTerm, setSearchTerm] = useState('');
  const [displayMode, setDisplayMode] = useState('grid');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');

  // Détecter le type d'appareil
  useEffect(() => {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const android = /Android/.test(navigator.userAgent);
    const mobile = iOS || android || window.innerWidth < 768;

    setDeviceType(mobile ? (iOS ? 'ios' : 'android') : 'desktop');
  }, []);
  
  // Pas de données simulées
  
  // Fonction pour convertir les produits de la structure de la DB au format attendu par l'UI
  const formatProductsFromDB = (productsData, userId) => {
    console.log("DEBUG - formatProductsFromDB - Données en entrée:", productsData);
    console.log("DEBUG - formatProductsFromDB - ID utilisateur:", userId);
    
    // Si userProducts n'est pas défini ou est vide
    if (!productsData) {
      console.log("DEBUG - formatProductsFromDB - Aucune donnée de produit");
      return [];
    }
    
    // Si userProducts est déjà un tableau, on le retourne tel quel
    if (Array.isArray(productsData)) {
      console.log("DEBUG - formatProductsFromDB - Données déjà sous forme de tableau");
      return productsData;
    }
    
    // Si userProducts est un objet avec des produits directs (comme indiqué par les logs)
    try {
      const formattedProducts = [];
      
      // On vérifie si l'objet a une structure plate (produits directement à la racine)
      const hasProducts = Object.values(productsData).some(value => 
        value && typeof value === 'object' && value.id && value.title
      );
      
      if (hasProducts) {
        console.log("DEBUG - formatProductsFromDB - Structure plate détectée avec produits directs");
        
        // Parcourir directement les clés de l'objet car ce sont des IDs de produits
        Object.keys(productsData).forEach(productId => {
          const product = productsData[productId];
          console.log("DEBUG - formatProductsFromDB - Traitement du produit direct:", productId, product);
          
          // Si c'est un objet produit valide
          if (product && product.id) {
            formattedProducts.push({
              ...product,
              // Ajouter des champs par défaut si nécessaire
              views: product.views || 0,
              sales: product.sales || 0,
              category: product.category || 'other'
            });
            console.log("DEBUG - formatProductsFromDB - Produit ajouté à la liste");
          } else {
            console.log(`DEBUG - formatProductsFromDB - Produit ignoré car invalide ou sans ID: ${productId}`);
          }
        });
      } 
      // Structure imbriquée par ID utilisateur (l'ancienne méthode)
      else if (userId && productsData[userId] && typeof productsData[userId] === 'object') {
        console.log(`DEBUG - formatProductsFromDB - Récupération directe des produits de l'utilisateur ${userId}`);
        
        Object.keys(productsData[userId]).forEach(productId => {
          const product = productsData[userId][productId];
          
          if (product && product.id) {
            formattedProducts.push({
              ...product,
              views: product.views || 0,
              sales: product.sales || 0,
              category: product.category || 'other'
            });
          }
        });
      } 
      // Parcourir tous les utilisateurs si pas de structure directe
      else {
        console.log("DEBUG - formatProductsFromDB - Parcours de tous les utilisateurs");
        
        Object.keys(productsData).forEach(userId => {
          if (productsData[userId] && typeof productsData[userId] === 'object') {
            Object.keys(productsData[userId]).forEach(productId => {
              const product = productsData[userId][productId];
              
              if (product && product.id) {
                formattedProducts.push({
                  ...product,
                  views: product.views || 0,
                  sales: product.sales || 0,
                  category: product.category || 'other'
                });
              }
            });
          }
        });
      }
      
      console.log("DEBUG - formatProductsFromDB - Produits après formatage:", formattedProducts);
      return formattedProducts;
    } catch (error) {
      console.error("DEBUG - Erreur lors du formatage des produits:", error);
      return [];
    }
  };

  // Formater les produits depuis la structure DB, sans données simulées de secours
  const allProducts = userProducts ? formatProductsFromDB(userProducts, id) : [];
  console.log("DEBUG - allProducts après formatage:", allProducts); // Log après formatage
  
  // Extraire les catégories à partir des produits réels
  const extractCategories = (products) => {
    const categoriesSet = new Set();
    
    // Ajouter les catégories par défaut
    categoriesSet.add('formation');
    categoriesSet.add('service');
    categoriesSet.add('ebook');
    
    // Ajouter les catégories des produits
    products.forEach(product => {
      if (product.category) {
        categoriesSet.add(product.category);
      }
    });
    
    // Convertir en tableau d'objets
    return Array.from(categoriesSet).map(cat => ({
      id: cat,
      name: cat.charAt(0).toUpperCase() + cat.slice(1) // Première lettre en majuscule
    }));
  };

  // Catégories dynamiques basées sur les produits disponibles
  const categories = extractCategories(allProducts);
  
  // Filtrer les produits selon les critères
  const filteredProducts = allProducts
    .filter(product => {
      // Log du produit pour comprendre sa structure
      console.log("DEBUG - Produit avant filtrage:", product);
      
      // Filtre de recherche
      const matchesSearch = product.title?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtre de catégorie (on utilise une catégorie par défaut si non définie)
      const productCategory = product.category || 'other';
      const matchesCategory = selectedCategory === 'all' || productCategory === selectedCategory;
      
      // Log des résultats du filtrage
      console.log(`DEBUG - Filtrage pour ${product.title}: matchesSearch=${matchesSearch}, matchesCategory=${matchesCategory}`);
      
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      // Tri
      switch(sortBy) {
        case 'price-asc':
          return (a.price || 0) - (b.price || 0);
        case 'price-desc':
          return (b.price || 0) - (a.price || 0);
        case 'views':
          return (b.views || 0) - (a.views || 0);
        case 'sales':
          return (b.sales || 0) - (a.sales || 0);
        case 'recent':
        default:
          // Tri par date en utilisant created_at ou updated_at
          const dateA = a.updated_at || a.created_at || 0;
          const dateB = b.updated_at || b.created_at || 0;
          return new Date(dateB) - new Date(dateA);
      }
    });
    
  console.log("DEBUG - filteredProducts après filtrage:", filteredProducts);
  
  // Gestionnaires d'événements
  const handleProductClick = (product) => {
    navigate(`/ProductCard/${product.id}`);
  };
  
  const handleActionClick = (product) => {
    setSelectedProduct(product);
  };
  
  const handleEditProduct = (product) => {
    navigate(`/editProduct/${product.id}`);
  };
  
  const handleDeleteProduct = (product) => {
    // Implémenter la logique de suppression
    console.log('Suppression du produit:', product.id);
  };
  
  // État pour la sélection du groupe/canal
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const { channels } = useContext(AppContext);
  
  // Fonction pour formater les canaux depuis la structure DB
  const formatChannelsFromDB = () => {
    // Si channels n'est pas défini ou est vide
    if (!channels) return [];
    
    try {
      // Si channels est déjà un tableau d'objets, on le retourne tel quel
      if (Array.isArray(channels)) return channels;
      
      // Si channels est un objet avec des ID comme clés
      const formattedChannels = Object.entries(channels).map(([channelId, channelData]) => ({
        id: channelId,
        nom: channelData.nom || channelData.title || 'Sans nom',
        type: channelData.type || 'groupe',
        // Très important : inclure l'ID Telegram réel du groupe, pas l'ID Firebase
        idGroup: channelData.idGroup || 'default'
      }));
      
      // Log pour debug
      console.log("Canaux formatés avec idGroup:", formattedChannels);
      
      return formattedChannels;
    } catch (error) {
      console.error("Erreur lors du formatage des canaux:", error);
      return [];
    }
  };
  
  // Liste formatée des canaux
  const channelsList = formatChannelsFromDB();
  
  // Fonction pour afficher la sélection de groupe/canal avant d'ajouter un produit
  const handleAddProduct = () => {
    console.log("handleAddProduct appelée, plateform:", deviceType);
    console.log("Canaux disponibles:", channelsList);
    
    // Vérifier s'il y a des groupes disponibles et s'ils ont des idGroup valides
    const hasValidGroups = channelsList.some(ch => ch.idGroup && ch.idGroup !== 'default');
    
    if (channelsList.length > 0 && hasValidGroups) {
      // Si des canaux valides sont disponibles, afficher le sélecteur
      setShowGroupSelector(true);
    } else {
      // S'il n'y a pas de groupe ou aucun groupe valide, rediriger vers RelierBot
      console.log("Aucun groupe valide disponible, redirection vers RelierBot");
      
      // Afficher une alerte avant de rediriger
      const message = channelsList.length === 0 
        ? "Aucun groupe ou canal n'est configuré. Vous allez être redirigé pour en ajouter un."
        : "Aucun groupe n'a d'identifiant Telegram valide. Vous allez être redirigé pour en configurer un correctement.";
      
      // Alerte simple
      alert(message);
      
      // Rediriger vers la page pour ajouter/configurer un groupe
      navigate('/RelierBot');
    }
  };
  
  // Fonction pour sélectionner un groupe et naviguer vers l'ajout de produit
  const selectGroupAndNavigate = (idGroup) => {
    setShowGroupSelector(false);
    console.log("Navigation vers l'ajout de produit avec idGroup:", idGroup);
    navigate(`/ajouterProduit/${idGroup}`);
  };
  
  // Afficher tous les produits ou un état vide
  const renderContent = () => {
    if (filteredProducts.length === 0) {
      // État vide simplifié
      return (
        <div className="mt-12 text-center px-4 py-12 bg-white rounded-2xl shadow-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <ShoppingBag className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm 
              ? `Aucun résultat pour "${searchTerm}"`
              : "Aucun produit"}
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            {searchTerm 
              ? "Essayez avec un autre terme ou modifiez vos filtres"
              : "Ajoutez votre premier produit pour démarrer"}
          </p> 
          <button 
            onClick={handleAddProduct}
            className="px-8 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            Ajouter un produit
          </button>
        </div>
      );
    }
    
    // Vue avec produits (grille ou liste)
    return (
      <>
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Vos produits <span className="text-sm font-normal text-gray-500">({filteredProducts.length})</span>
          </h2>
          
          <div className="flex gap-2">
            <button 
              onClick={() => setDisplayMode('grid')}
              className={`p-2 rounded-lg ${
                displayMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
              aria-label="Vue en grille"
            >
              <Grid className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setDisplayMode('list')}
              className={`p-2 rounded-lg ${
                displayMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
              aria-label="Vue en liste"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Affichage des produits selon le mode */}
        {displayMode === 'grid' ? (
          <ProductGrid 
            products={filteredProducts} 
            onProductClick={handleProductClick}
            onActionClick={handleActionClick}
          />
        ) : (
          <ProductList 
            products={filteredProducts} 
            onProductClick={handleProductClick}
            onActionClick={handleActionClick}
          />
        )}
      </>
    );
  };

  // Vérifier si l'application s'exécute dans le WebApp Telegram
  const isTelegramWebApp = typeof window !== 'undefined' && window.Telegram && window.Telegram.WebApp;
  
  // Configurer Telegram WebApp si disponible
  useEffect(() => {
    if (isTelegramWebApp) {
      const webApp = window.Telegram.WebApp;
      webApp.ready();
      webApp.expand();
      
      // Configuration du bouton principal de Telegram pour ajouter un produit
      webApp.MainButton.setParams({
        text: 'AJOUTER UN PRODUIT',
        color: '#0066FF',
        textColor: '#FFFFFF',
        is_active: true,
        is_visible: true
      });
      
      // Utiliser le même comportement que sur desktop pour être cohérent
      webApp.MainButton.onClick(() => {
        console.log("MainButton clicked, canaux:", channelsList);
        handleAddProduct();
      });
      webApp.MainButton.show();
      
      // Configurer la back button si nécessaire
      webApp.BackButton.onClick(() => navigate('/IndexEspace'));
      webApp.BackButton.show();
      
      return () => {
        webApp.MainButton.hide();
        webApp.BackButton.hide();
      };
    }
  }, [navigate]);
  
  // Gestion des erreurs globale pour éviter les erreurs liées aux null/undefined
  const handleError = (error) => {
    console.error('Error in ProductsList:', error);
    // Gérer l'erreur de manière élégante (pourrait montrer une notification)
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-28" style={{ paddingTop: isTelegramWebApp ? '0px' : '60px' }}>
      {/* Header avec navigation - affiché uniquement si ce n'est pas une WebApp Telegram */}
      {!isTelegramWebApp && (
        <header className="bg-white shadow-sm sticky top-0 z-20">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
            <button 
              onClick={() => navigate('/IndexEspace')}
              className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h1 className="text-xl font-bold text-gray-900 ml-2">Catalogue</h1>
            
            <div className="ml-auto flex gap-2">
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg ${showFilters ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'} transition-colors`}
              >
                <Filter className="w-5 h-5" />
              </button>
              <button 
                onClick={handleAddProduct}
                className="p-2 rounded-lg bg-blue-600 text-white"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>
      )}
      
   {
    /*

    
    
    */
   }
      
      {/* Contenu principal */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        {renderContent()}
      </div>
      
      {/* Popup d'options d'un produit */}
      {selectedProduct && (
        <ProductOptions 
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onEdit={handleEditProduct}
          onDelete={handleDeleteProduct}
          onView={handleProductClick}
        />
      )}
      
      {/* Popup de sélection de groupe/canal */}
      {showGroupSelector && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center animate-fadeIn backdrop-blur-sm safe-area-bottom"
          onClick={() => setShowGroupSelector(false)}
        >
          <div 
            className="bg-white rounded-xl p-4 w-full max-w-lg mx-4 animate-fadeIn"
            style={{ maxHeight: '80vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Choisir un groupe/canal</h3>
              <button
                onClick={() => setShowGroupSelector(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Choisir un groupe/canal</h3>
            
            <div className="space-y-3 mb-4">
              {/* Option pour le groupe par défaut */}
             {
              /*
               <button 
                onClick={() => selectGroupAndNavigate('default')}
                className="w-full flex items-center p-3 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
              >
                <div className="p-2 bg-blue-100 rounded-full mr-3">
                  <ShoppingBag className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Boutique par défaut</p>
                  <p className="text-xs text-gray-500">
                    Produits sans groupe spécifique
                  </p>
                </div>
              </button>
              
              */
             }
              {/* Liste des canaux/groupes disponibles */}
              {channelsList.map((channel) => (
                <button 
                  key={channel.id}
                  onClick={() => selectGroupAndNavigate(channel.idGroup)}
                  className={`w-full flex items-center p-3 ${channel.idGroup && channel.idGroup !== 'default' ? 'bg-blue-50 hover:bg-blue-100' : 'bg-gray-50 hover:bg-gray-100'} rounded-xl transition-colors`}
                >
                  <div className="p-2 bg-blue-100 rounded-full mr-3">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">{channel.nom || 'Groupe'}</p>
                    <p className="text-xs text-gray-500">
                      {channel.type === 'canal' ? 'Canal' : 'Groupe'} 
                      {channel.idGroup && channel.idGroup !== 'default' ? 
                        <span className="inline-flex items-center px-2 py-0.5 ml-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ID Telegram: {channel.idGroup}
                        </span> : 
                        <span className="inline-flex items-center px-2 py-0.5 ml-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          ID manquant
                        </span>
                      }
                    </p>
                  </div>
                </button>
              ))}
              
              {channelsList.length === 0 && (
                <div className="text-center py-6 text-gray-500">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p>Aucun groupe ou canal disponible</p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={() => navigate('/RelierBot')}
                className="flex-1 py-3 bg-blue-50 text-blue-600 rounded-xl font-medium"
              >
                Ajouter un groupe
              </button>
              <button 
                onClick={() => setShowGroupSelector(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium"
              >
                Annuler
              </button>
            </div>
            
            {/* Message d'avertissement si aucun groupe n'a d'ID Telegram valide */}
            {!channelsList.some(ch => ch.idGroup && ch.idGroup !== 'default') && channelsList.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 font-medium">Attention</p>
                <p className="text-sm text-amber-700">
                  Aucun groupe n'a d'identifiant Telegram valide. Ajoutez un nouveau groupe ou contactez le support.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Bouton d'ajout flottant - seulement si ce n'est pas une WebApp Telegram sur mobile */}
      {(!isTelegramWebApp || window.innerWidth > 768) && (
        <div className="fixed bottom-6 right-6" style={{ bottom: isTelegramWebApp ? '80px' : '24px' }}>
          <button
            onClick={handleAddProduct}
            className="w-14 h-14 bg-blue-600 rounded-full text-white shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
      )}
      
      {/* CSS pour optimiser l'expérience mobile et Telegram */}
      <style jsx="true">{`
        .hide-scrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out forwards;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
        
        /* Styles pour Telegram WebApp */
        @media (max-width: 768px) {
          button:active {
            opacity: 0.8;
          }
          
          /* Éviter que le content ne soit sous la safe area (notch des iPhones) */
          .safe-area-top {
            padding-top: env(safe-area-inset-top, 0px);
          }
          
          .safe-area-bottom {
            padding-bottom: env(safe-area-inset-bottom, 24px);
          }
        }
      `}</style>
    </div>
  );
};

export default ProductsList;